import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  CONTAINER_STYLE,
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  SCHOOL_RESOURCE_TYPES,
  COLORS,
} from '@helpers/constants'
import SubHeader from '@views/SubHeader'
import Container from '@components/Container'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Text from '@components/Text'
import Loader from '@components/Loader'
import Button from '@components/Button'
import SelectButton from '@components/SelectButton'
import GridColumns from '@components/GridColumns'
import StudentList from '@components/StudentList'
import SharedBooksList from '@components/SharedBooksList'
import SchoolResourceList from '@components/SchoolResourceList'
import { useUpdateSchoolClass } from '@hooks/mutations/useUpdateSchoolClass'
import { useDemoResources } from '@hooks/useDemoResources'
import { useConfirmAddDemoSchoolGroup } from '@hooks/useConfirmAddDemoSchoolGroup'
import ImageGallery from '@components/ImageGallery'
import { useSchoolClassesOptions } from '@hooks/useSchoolClassesOptions'
import { storagePath } from '@helpers/variables'
import withTooltip from '@components/WithTooltip'
import { useBooks } from '@hooks/useBooks'

const ButtonWithTooltip = withTooltip(Button)

const SingleDemoSchoolClass = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showGallery, setShowGallery] = useState(false)
  const { confirmAddDemoSchoolGroup } = useConfirmAddDemoSchoolGroup()
  const [booksWithRelatedSchoolGroups, setBooksWithRelatedSchoolGroups] = useState()
  const [relatedSchoolGroups, setRelatedSchoolGroups] = useState()
  const [demoSchoolClass, setDemoSchoolClass] = useState()
  const breadcrumb = useMemo(() => (demoSchoolClass && {
    label: demoSchoolClass?.name,
  }), [demoSchoolClass])
  const { books, isLoading: isLoadingBooks } = useBooks()
  const {
    demoSchoolClasses,
    demoSchoolGroups,
    error,
    // isLoading: demoResourcesLoading,
  } = useDemoResources()
  const { schoolClassesOptions } = useSchoolClassesOptions()
  const images = [
    {
      original: storagePath + '/demo-slide/elev-bokhylla.jpg',
      description: <p>När eleven loggar in på sin Doklet ser hen sina böcker och vilken lärare som har delat boken. En elev kan <i>inte</i> se böckers årskurs och svårighetsnivå. En elev kan heller inte se vilken eller vilka eventuella grupper hen tillhör.</p>,
    },
    {
      original: storagePath + '/demo-slide/elev-bok.jpg',
      description: <p>När eleven har öppnat en bok visas bokens avsnitt i vänsterkanten. Eleven kan klicka in sig på aktuellt avsnitt för att läsa och göra övningar.</p>,
    },
  ]

  useEffect(() => {
    if (demoSchoolClasses?.length && !isLoadingBooks && books?.length > 0) {
      const demoSchoolClass = demoSchoolClasses.find(sc => sc.id === id)

      const studentIds = demoSchoolClass.students.map(student => student.id)
      const groups = demoSchoolGroups?.filter(sg => sg.students.some(student => studentIds.includes(student.id)))
      const uniqueGroups = groups.reduce((acc, group) => acc.every(unique => unique.id !== group.id) ? [...acc, group] : acc, [])
      setRelatedSchoolGroups(uniqueGroups)

      const booksWithGroups = uniqueGroups
        .flatMap((group) =>
          group.books
            .map(({ id, pivot }) => ({ ...(books.find(book => book.id === id)), pivot }))
            .map((book) => ({
              ...book,
              schoolGroups: uniqueGroups.filter((schoolGroup) =>
                schoolGroup.books.some(({ id }) => id === book.id)
              ),
            }))
        )
        .reduce(
          (acc, book) =>
            acc.every((unique) => unique.id !== book.id) ? [...acc, book] : acc,
          []
        )

      setBooksWithRelatedSchoolGroups([...demoSchoolClass.books, ...booksWithGroups])
      setDemoSchoolClass(demoSchoolClass)
    }
  }, [demoSchoolClasses, isLoadingBooks, books.length])

  return (
    <>
      <SubHeader breadcrumb={breadcrumb} >
        <SubHeader.MiddleSection />
        <SubHeader.RightSection>
          {demoSchoolClass && schoolClassesOptions.length > 0 && (
            <SelectButton
              onChange={(e) => {
                navigate(e.target.value)
              }}
              name={'demoSchoolClass'}
              id={'demoSchoolClass'}
              options={schoolClassesOptions}
              defaultValue={`demoklass/${demoSchoolClass.id}`}
              extraClasses={'max-w-[200px]'}
            />
          )}
        </SubHeader.RightSection>
      </SubHeader>

      {!demoSchoolClass && !error && <Loader />}
      {error && <Text>Någonting gick fel när vi skulle hämta klass</Text>}

      {demoSchoolClass && !error && (
        <div
          className={'flex flex-col'}
          style={{ minHeight: 'calc(100vh - 146px)' }}
        >
          <div
            className={
              'absolute bg-demoGreen w-min pr-16 pl-4 py-2 right-0 top-[178px]'
            }
          >
            Demo
          </div>
          <Container size={CONTAINER_STYLE.DESKTOP}>
            <div className={'flex justify-center'}>
              <Heading
                headingLevel={HEADING_TYPE.H1}
                size={TEXT_SIZES.XXLARGE}
                color={COLORS.DARK_DEMO_GREEN.TEXT}
              >
                {demoSchoolClass?.name}
              </Heading>
            </div>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <div className={'mx-auto max-w-[700px]'}>
              <Text>
                Det här är en demoklass med demoelever. Använd den för att prova
                Doklets funktioner i lugn och ro, utan att behöva involvera dina
                egna elever. <br />
                <br />
                När du känner dig säker på hur allt fungerar är det bara att
                skriva in dina riktiga elever i “Min klass”.
              </Text>
            </div>

            <Divider size={DIVIDER_SIZE.BASE} />

            <div className='flex justify-between'>
              <ButtonWithTooltip
                type={BUTTON_TYPE.SECONDARY}
                size={BUTTON_SIZE.MEDIUM}
                disabled={demoSchoolGroups.length >= 2}
                icon={'PlusCircle'}
                iconLeft
                margin
                handleClick={confirmAddDemoSchoolGroup}
                tooltip={
                  demoSchoolGroups.length >= 2
                    ? 'Max antal demogrupper är skapade.'
                    : 'Här kan du dela in demo-elever i olika grupper.'
                }
              >
                Skapa demogrupp
              </ButtonWithTooltip>

              <Button
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.MEDIUM}
                margin
                handleClick={() => {
                  setShowGallery(true)
                }}
              >
                Visa hur Doklet ser ut för en elev
              </Button>
            </div>
          </Container>

          <Divider line size={DIVIDER_SIZE.SMALL} />

          <div className={'xl:container xl:mx-auto px-10 flex-auto h-[1px]'}>
            <GridColumns cols={4} classes={'h-full gap-7'}>
              <div className={'h-full border-r border-mediumGrey'}>
                <Divider size={DIVIDER_SIZE.BASE} />
                <StudentList
                  items={demoSchoolClass.students}
                  resourceType={SCHOOL_RESOURCE_TYPES.DEMO_CLASS}
                  resourceId={demoSchoolClass.id}
                  updateHook={useUpdateSchoolClass}
                  hideRemoveButton
                />
                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
              <div className={'col-span-3'}>
                <Divider size={DIVIDER_SIZE.BASE} />

                {booksWithRelatedSchoolGroups && (
                  <SharedBooksList
                    items={booksWithRelatedSchoolGroups}
                    resourceType={SCHOOL_RESOURCE_TYPES.DEMO_CLASS}
                    resource={demoSchoolClass}
                  />
                )}

                <Divider size={DIVIDER_SIZE.BASE} />

                {relatedSchoolGroups?.length > 0 && (
                  <>
                    <Heading
                      headingLevel={HEADING_TYPE.H2}
                      size={TEXT_SIZES.XLARGE}
                    >
                      {`Klassens grupper`}
                    </Heading>

                    <Divider size={DIVIDER_SIZE.SMALL} />

                    <SchoolResourceList
                      demoItems={relatedSchoolGroups}
                      gridCols={2}
                      resourceType={SCHOOL_RESOURCE_TYPES.GROUP}
                    />
                  </>
                )}

                <Divider size={DIVIDER_SIZE.BASE} />
              </div>
            </GridColumns>
          </div>
        </div>
      )}

      <ImageGallery
        images={images}
        onClose={() => {
          setShowGallery(false)
        }}
        show={showGallery}
      />
    </>
  )
}

export default SingleDemoSchoolClass
